'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';
import { notificationApi } from '@/services/api/notification.api';

export const NOTIFICATION_QUERY_KEYS = {
  all: ['notifications'] as const,
  list: (limit: number) => [...NOTIFICATION_QUERY_KEYS.all, 'list', limit] as const,
  unreadCount: () => [...NOTIFICATION_QUERY_KEYS.all, 'unread-count'] as const,
};

/**
 * Poll the unread badge count. Lightweight; drives the header bell.
 */
export const useUnreadNotificationCount = () => {
  const { isAuthenticated } = useAuth();
  return useQuery<number>({
    queryKey: NOTIFICATION_QUERY_KEYS.unreadCount(),
    queryFn: () => notificationApi.getUnreadCount(),
    enabled: isAuthenticated,
    refetchInterval: 45_000,
    staleTime: 30_000,
  });
};

/**
 * Fetch the notifications list (first page). Only runs when the panel is open
 * (pass `enabled`) so we don't fetch the full list just to show the badge.
 */
export const useNotifications = (limit = 20, enabled = true) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.list(limit),
    queryFn: () => notificationApi.getNotifications({ limit, offset: 0 }),
    enabled: isAuthenticated && enabled,
    staleTime: 15_000,
  });
};

/**
 * Mark notifications read (specific ids, or all when omitted). Refreshes the
 * list and the badge count on success.
 */
export const useMarkNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids?: number[]) => notificationApi.markRead(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.all });
    },
  });
};
